import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';

// Eagerly loaded — used globally on every page
import Modal from './components/Modal';
import CookieBanner from './components/CookieBanner';
import CookiePreferences from './components/CookiePreferences';
import { initializeAnalytics } from './utils/analytics';
import { hasConsent, hasAnyConsent, COOKIE_CATEGORIES } from './utils/cookies';
import PageLoadingFallback from './components/PageLoadingFallback';

// Route-level code splitting — each page loads only when navigated to
const Homepage = lazy(() => import('./pages/Homepage'));
const ProductBrowsePage = lazy(() => import('./pages/ProductBrowsePage'));
const LikedProductsPage = lazy(() => import('./pages/LikedProductsPage'));
const SharedListPage = lazy(() => import('./pages/SharedListPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const StoresPage = lazy(() => import('./pages/StoresPage'));
const Profile = lazy(() => import('./pages/Profile'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SignIn = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
function AppRoutes() {
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookiePreferences, setShowCookiePreferences] = useState(false);
  const [signInMessage, setSignInMessage] = useState(null);
  const location = useLocation();

  const { currentUser } = useAuth();
  const scrollPosition = useRef(0);

  // CRITICAL: Always ensure body scrolling is enabled on mount and route changes
  useEffect(() => {
    // Always restore body scrolling - never allow it to be locked
    document.body.style.overflow = '';
    document.body.style.overflowY = '';
    document.body.style.overflowX = '';

    // Also ensure html can scroll
    document.documentElement.style.overflow = '';
    document.documentElement.style.overflowY = '';
    document.documentElement.style.overflowX = '';
  }, [location]);

  // Also restore on initial mount
  useEffect(() => {
    document.body.style.overflow = '';
    document.body.style.overflowY = '';
    document.body.style.overflowX = '';
    document.documentElement.style.overflow = '';
    document.documentElement.style.overflowY = '';
    document.documentElement.style.overflowX = '';
  }, []);

  // Handlers to open/close modals
  const openSignIn = (message) => {
    scrollPosition.current = window.scrollY;
    setSignInMessage(message || null);
    setShowSignIn(true);
  };
  const closeSignIn = () => setShowSignIn(false);
  const openSignUp = () => {
    scrollPosition.current = window.scrollY;
    setShowSignUp(true);
  };
  const closeSignUp = () => setShowSignUp(false);

  // Auto-close modals on successful authentication and restore scroll position
  useEffect(() => {
    if (currentUser) {
      setShowSignIn(false);
      setShowSignUp(false);
      setTimeout(() => {
        window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      }, 0);
    }
  }, [currentUser]);

  // Initialize analytics if consent is given
  useEffect(() => {
    if (hasConsent(COOKIE_CATEGORIES.ANALYTICS)) {
      initializeAnalytics();
    }
  }, []);

  // Listen for consent updates
  useEffect(() => {
    const handleConsentUpdate = (event) => {
      if (event.detail && event.detail[COOKIE_CATEGORIES.ANALYTICS]) {
        initializeAnalytics();
      }
    };

    window.addEventListener('cookieConsentUpdated', handleConsentUpdate);
    return () => window.removeEventListener('cookieConsentUpdated', handleConsentUpdate);
  }, []);

  // Show cookie banner modal on first visit (if no consent)
  useEffect(() => {
    if (!hasAnyConsent()) {
      setShowCookieBanner(true);
    }
  }, []);

  // Listen for cookie settings open request from footer
  useEffect(() => {
    const handleOpenCookiePreferences = () => {
      setShowCookiePreferences(true);
    };

    window.addEventListener('openCookiePreferences', handleOpenCookiePreferences);
    return () => window.removeEventListener('openCookiePreferences', handleOpenCookiePreferences);
  }, []);

  // Handler to open cookie preferences from banner
  const handleOpenCookiePreferences = () => {
    setShowCookiePreferences(true);
  };

  // Pass modal handlers to PageLayout via context or props
  const pageLayoutProps = {
    openSignIn,
    openSignUp
  };

  return (
    <>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/" element={<Homepage {...pageLayoutProps} />} />
          <Route path="/browse" element={<ProductBrowsePage {...pageLayoutProps} />} />
          <Route path="/liked" element={<LikedProductsPage {...pageLayoutProps} />} />
          <Route path="/community" element={<Navigate to="/" replace />} />
          <Route path="/shared-list/:listId" element={<SharedListPage {...pageLayoutProps} />} />
          <Route path="/about" element={<AboutUsPage {...pageLayoutProps} />} />
          <Route path="/stores" element={<StoresPage {...pageLayoutProps} />} />
          <Route path="/profile" element={<Profile {...pageLayoutProps} />} />
          <Route path="/privacy" element={<PrivacyPolicy {...pageLayoutProps} />} />
          <Route path="/cookies" element={<CookiesPage {...pageLayoutProps} />} />
          <Route path="/terms" element={<TermsOfService {...pageLayoutProps} />} />
          <Route path="/contact" element={<ContactPage {...pageLayoutProps} />} />
        </Routes>
      </Suspense>
      <Modal isOpen={showSignIn} onClose={closeSignIn}>
        <Suspense fallback={null}>
          <SignIn onSwitchToSignUp={() => { closeSignIn(); openSignUp(); }} message={signInMessage} />
        </Suspense>
      </Modal>
      <Modal isOpen={showSignUp} onClose={closeSignUp}>
        <Suspense fallback={null}>
          <SignUp onSwitchToSignIn={() => { closeSignUp(); openSignIn(); }} />
        </Suspense>
      </Modal>
      <CookieBanner
        isOpen={showCookieBanner}
        onClose={() => setShowCookieBanner(false)}
        onOpenPreferences={handleOpenCookiePreferences}
        requireChoice={!hasAnyConsent()}
      />
      <CookiePreferences
        isOpen={showCookiePreferences}
        onClose={() => setShowCookiePreferences(false)}
        requireChoice={!hasAnyConsent()}
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
